import i18n from "@/i18n"
import { analytics } from "@/telemetry/amplitude"
import { Sentry } from "@/telemetry/sentry"
import { isAbortError } from "@/utils/error"
import toast from "react-hot-toast"
import { hasPermission } from "./file-system"

let isPickingDirectory = false

export type DirectoryType = "import_photo" | "export_photo" | "export_preset"
const getDirectoryId = (type: DirectoryType) => {
	switch (type) {
		case "import_photo":
			return "import-directory"
		case "export_photo":
			return "export-directory"
		case "export_preset":
			return "export-preset-directory"
	}
}

const getStartDirectory = (type: DirectoryType) => {
	switch (type) {
		case "import_photo":
			return undefined
		case "export_photo":
			return "pictures"
		case "export_preset":
			return "downloads"
	}
}

/** Show directory picker and grant `readwrite` permission on the selected directory and validate the directory */
/** Preferred method if the directory needs to be valid and accessible */
export const chooseAndValidateDirectory = async (
	type: DirectoryType,
	startDirectory?: FileSystemDirectoryHandle,
	skipHandleErrors: DirectoryError[] = [],
) => {
	const directory = await chooseDirectory(type, startDirectory)
	const result = await checkDirectoryIsValid(directory, skipHandleErrors)

	const info = { selectMethod: "directory_picker", type }
	if (result instanceof FileSystemDirectoryHandle) {
		analytics.trackEvent({
			label: "folder_selected",
			type: "event",
			customProperties: info,
		})
	} else {
		analytics.trackEvent({
			label: "folder_select_error",
			type: "error",
			customProperties: info,
			errorReason: result,
		})
	}

	return result
}

/** Show directory picker and grant `readwrite` permission on the selected directory. */
export const chooseDirectory = async (
	type: DirectoryType,
	startDirectory?: FileSystemDirectoryHandle,
) => {
	if (isPickingDirectory) {
		return undefined
	}
	isPickingDirectory = true

	try {
		const directory = await window.showDirectoryPicker({
			mode: "readwrite",
			id: getDirectoryId(type),
			startIn: startDirectory ?? getStartDirectory(type),
		})

		return directory
	} catch (e) {
		if (isAbortError(e)) {
			return undefined
		}
		Sentry.captureException(e)
	} finally {
		isPickingDirectory = false
	}
}

export type DirectoryError = "no_handle" | "no_directory" | "no_permission" | "no_access"
export async function checkDirectoryIsValid(
	handle: FileSystemDirectoryHandle | undefined,
	skipHandleErrors: DirectoryError[] = [],
): Promise<DirectoryError | FileSystemDirectoryHandle> {
	try {
		if (!handle) {
			if (!skipHandleErrors.includes("no_handle")) {
				toast.error(i18n.t("components:openDirectory.noDirectorySelectedPleaseTry"))
			}
			return "no_handle"
		}

		if (!(await hasPermission(handle, "readwrite"))) {
			if (!skipHandleErrors.includes("no_permission")) {
				toast.error(i18n.t("components:openDirectory.weNeedPermissionToAccess"))
			}
			return "no_permission"
		}

		// There is no Filesystem API call to check if directory exists
		// Instead, we will test writing and deleting a placeholder file
		const tempFileName = `.placeholder-${new Date().getTime()}`
		await handle.getFileHandle(tempFileName, { create: true })
		await handle.removeEntry(tempFileName)
		return handle
	} catch (err) {
		if (
			err instanceof Error &&
			(err.name === "NotAllowedError" || err.name === "NoModificationAllowedError")
		) {
			if (!skipHandleErrors.includes("no_access")) {
				toast.error(i18n.t("components:openDirectory.yourFolderIsNotAccessible"), {
					duration: 5000,
				})
			}
			return "no_access"
		} else {
			if (!skipHandleErrors.includes("no_directory")) {
				toast.error(i18n.t("components:openDirectory.noDirectoryAvailableAtThe"), {
					duration: 5000,
				})
			}
			return "no_directory"
		}
	}
}
